import { motion } from "framer-motion";
import { fadeUp, floatingAnimation } from "@/lib/animations";
import { useState, useRef, useEffect } from "react";

import GrapeGraphIntroVideo from "@assets/GrapeGraph.ai Intro.mp4";

export default function HeroSection() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasThumbnail, setHasThumbnail] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // This effect attempts to load metadata and generate a thumbnail from the video
  useEffect(() => {
    const videoElement = videoRef.current;
    if (videoElement) {
      // Try to load metadata to get the first frame as a poster
      const handleLoadedMetadata = () => {
        try {
          // Create a canvas element
          const canvas = document.createElement('canvas');
          canvas.width = videoElement.videoWidth;
          canvas.height = videoElement.videoHeight;
          
          // Draw the video frame
          const ctx = canvas.getContext('2d');
          if (ctx) {
            // After metadata is loaded, seek to a frame that's representative of the video
            videoElement.currentTime = 0.5; // Seek to 0.5 seconds in

            // Once we've seeked, draw the frame
            videoElement.addEventListener('seeked', function onSeeked() {
              // Draw the video frame on the canvas
              ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
              
              // Convert to data URL
              try {
                const dataUrl = canvas.toDataURL('image/jpeg');
                
                // Set as poster
                videoElement.poster = dataUrl;
                setHasThumbnail(true);
                
                // Reset current time
                videoElement.currentTime = 0;
              } catch (e) {
                console.error("Error generating thumbnail:", e);
              }
              
              // Remove this event listener
              videoElement.removeEventListener('seeked', onSeeked);
            }, { once: true });
          }
        } catch (e) {
          console.error("Error setting up thumbnail generation:", e);
        }
      };

      videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
      
      return () => {
        videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
    }
  }, []);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        // Pause all other videos first
        document.querySelectorAll('video').forEach(video => {
          if (video !== videoRef.current) {
            video.pause();
          }
        });
        
        videoRef.current.play();
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };
  return (
    <section 
      id="hero" 
      className="pt-28 pb-20 md:pt-32 md:pb-32 bg-gradient-to-br from-purple-50 to-white relative overflow-hidden"
    >
      {/* Background grid pattern */}
      <div className="absolute inset-0 opacity-10">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="smallGrid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-purple-600" />
            </pattern>
            <pattern id="grid" width="100" height="100" patternUnits="userSpaceOnUse">
              <rect width="100" height="100" fill="url(#smallGrid)" />
              <path d="M 100 0 L 0 0 0 100" fill="none" stroke="currentColor" strokeWidth="1" className="text-purple-600" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col md:flex-row items-center md:gap-8 lg:gap-16">
          <div className="w-full md:w-3/5 md:pr-6">
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-purple-600"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={0}
            >
              AI-powered video content that converts
            </motion.h1>
            
            <motion.p 
              className="text-lg md:text-xl mb-8 text-gray-700 leading-relaxed"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={2}
            >
              Transform your social media presence with our AI-driven reels and short-form videos. Designed to engage, convert, and build your brand on autopilot.
            </motion.p>
            
            <motion.div
              className="flex flex-col sm:flex-row gap-4 mb-8"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={3}
            >
              <a 
                href="#reels" 
                className="px-8 py-4 bg-purple-600 text-white font-medium rounded-lg shadow-lg hover:bg-purple-700 transition-all text-center"
              >
                View Our Work
              </a>
              <a 
                href="#contact" 
                className="px-8 py-4 border-2 border-green-500 text-green-600 font-medium rounded-lg hover:bg-green-50 transition-all text-center"
              >
                Get In Touch
              </a>
            </motion.div>
            
            <motion.div
              className="flex items-center"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={4}
            >
              <div className="flex -space-x-2">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="w-10 h-10 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center overflow-hidden">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                ))}
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Trusted by <span className="text-purple-600 font-semibold">150+</span> brands</p>
              </div>
            </motion.div>
          </div>
          
          <div className="w-full md:w-2/5 mt-12 md:mt-0 max-w-[280px] mx-auto">
            <motion.div 
              className="relative rounded-xl shadow-xl overflow-hidden mx-auto"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={2}
            >
              {/* Instagram-style Reel with 9:16 aspect ratio */}
              <div className="aspect-[9/16] bg-gradient-to-b from-purple-600 to-purple-500 relative overflow-hidden">
                {/* Wrapper div to handle click events */}
                <div 
                  className="absolute inset-0 cursor-pointer z-10"
                  onClick={togglePlayPause}
                >
                  {/* Video element playing the intro video */}
                  <video 
                    ref={videoRef}
                    className="absolute inset-0 w-full h-full object-cover"
                    preload="metadata"
                    poster={hasThumbnail ? undefined : "/videos/thumbnail.svg"} 
                    crossOrigin="anonymous"
                    playsInline
                  >
                    <source src={GrapeGraphIntroVideo} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                  
                  {/* Play button overlay - only show when not playing */}
                  {!isPlaying && (
                    <div className="absolute inset-0 flex items-center justify-center z-10">
                      <div className="w-20 h-20 rounded-full bg-white/90 flex items-center justify-center hover:scale-105 transition-transform duration-300">
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          className="h-10 w-10 text-purple-600" 
                          viewBox="0 0 20 20" 
                          fill="currentColor"
                        >
                          <path 
                            fillRule="evenodd" 
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" 
                            clipRule="evenodd" 
                          />
                        </svg>
                      </div>
                    </div>
                  )}
                  
                  {/* Pause button overlay - only show when playing and hovering */}
                  {isPlaying && (
                    <div className="absolute inset-0 flex items-center justify-center z-10 opacity-0 hover:opacity-100 transition-opacity">
                      <div className="w-20 h-20 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center hover:bg-black/40 transition-all duration-300 border border-white/30">
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          className="h-10 w-10 text-white" 
                          viewBox="0 0 20 20" 
                          fill="currentColor"
                        >
                          <path 
                            fillRule="evenodd" 
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v4a1 1 0 11-2 0V8z" 
                            clipRule="evenodd" 
                          />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Graph pattern overlay */}
                <div className="absolute inset-0 opacity-15 mix-blend-overlay pointer-events-none">
                  <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <pattern id="heroGrid" width="20" height="20" patternUnits="userSpaceOnUse">
                        <path d="M 20 0 L 0 0 0 20" fill="none" stroke="white" strokeWidth="0.5" />
                      </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#heroGrid)" />
                  </svg>
                </div>
                
                {/* Top section - account info */}
                <div className="absolute top-0 left-0 right-0 p-3 flex items-center">
                  <div className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center overflow-hidden mr-2">
                    <span className="text-xs font-bold text-purple-600">GG</span>
                  </div>
                  <span className="text-white text-xs font-medium">@grapegraphai</span>
                  <button className="ml-auto text-white text-xs font-semibold px-2 py-1 rounded-md border border-white/30">
                    Follow
                  </button>
                </div>
                
                {/* Right side - interaction buttons */}
                <div className="absolute right-3 bottom-20 flex flex-col space-y-4">
                  {/* Like button */}
                  <div className="flex flex-col items-center">
                    <button className="text-white">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    </button>
                    <span className="text-white text-xs mt-1">3.2k</span>
                  </div>
                  {/* Comment button */}
                  <div className="flex flex-col items-center">
                    <button className="text-white">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </button>
                    <span className="text-white text-xs mt-1">492</span>
                  </div>
                  {/* Share button */}
                  <div className="flex flex-col items-center">
                    <button className="text-white">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                      </svg>
                    </button>
                  </div>
                </div>
                
                {/* Bottom section - title & audio */}
                <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent">
                  <div className="text-white text-sm font-medium mb-2">
                    AI-Powered Video Content
                  </div>
                  
                  {/* Music info */}
                  <div className="flex items-center text-white/80 text-xs">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                    </svg>
                    <span className="truncate max-w-[120px]">Original Audio - GrapeGraph AI</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>


      
      {/* Decorative Elements */}
      <motion.div
        className="absolute top-20 left-10 w-24 h-24 rounded-full bg-green-400/20"
        variants={floatingAnimation}
        initial="initial"
        animate="float"
      />
      
      <motion.div
        className="absolute bottom-10 right-10 w-16 h-16 rounded-full bg-purple-600/20"
        variants={floatingAnimation}
        initial="initial"
        animate="float"
        custom={1}
      />
    </section>
  );
}
